import { Bookmark, Check, ChevronRight } from 'lucide-react';
import { useState, type MouseEvent } from 'react';
import { styleColorHex, type TurbanStyle } from '@/data/styles';
import { StyleVisual } from '@/components/StyleVisual';

export function StyleCard({ style, onOpen, featured = false }: { style: TurbanStyle; onOpen?: (style: TurbanStyle) => void; featured?: boolean }) {
  const [saved, setSaved] = useState(() => typeof window !== 'undefined' && JSON.parse(localStorage.getItem('turbans-favourites') || '[]').includes(style.id));
  const [previewColour, setPreviewColour] = useState<string | null>(null);
  const toggleSave = (event: MouseEvent) => {
    event.stopPropagation();
    const current: string[] = JSON.parse(localStorage.getItem('turbans-favourites') || '[]');
    const next = current.includes(style.id) ? current.filter((id) => id !== style.id) : [...current, style.id];
    localStorage.setItem('turbans-favourites', JSON.stringify(next));
    window.dispatchEvent(new Event('turbans-favourites-changed'));
    setSaved(!saved);
  };
  return (
    <article
      className={`group relative overflow-hidden rounded-[1.4rem] border border-border bg-card text-left transition duration-300 hover:-translate-y-1 hover:shadow-lg focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 ${featured ? 'md:col-span-2' : ''}`}
      data-testid={`card-style-${style.id}`}
    >
      <div
        className="absolute inset-0 z-0 cursor-pointer focus:outline-none"
        onClick={() => onOpen?.(style)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen?.(style);
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={`View details for ${style.name}`}
      />
      <div className="relative pointer-events-none flex min-h-[238px] items-center justify-center overflow-hidden" style={{ background: `linear-gradient(135deg, ${style.accent} 0%, #152436 100%)` }}>
        <span className="absolute left-5 top-5 rounded-full border border-white/20 px-2.5 py-1 mono-font text-[9px] uppercase tracking-[.16em] text-white/75">{style.region.split(' · ')[0]}</span>

        <button
          onClick={toggleSave}
          aria-label={saved ? `Remove ${style.name} from saved` : `Save ${style.name}`}
          className="absolute right-5 top-5 z-10 pointer-events-auto rounded-full bg-black/20 p-2.5 text-white backdrop-blur-md transition hover:bg-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          data-testid={`button-save-style-${style.id}`}
        >
          {saved ? <Bookmark size={17} fill="currentColor" /> : <Bookmark size={17} />}
        </button>
        <StyleVisual
          style={style}
          className="absolute inset-0 transition duration-500 group-hover:scale-105"
          imageClassName="absolute inset-0 h-full w-full object-cover object-[center_28%] transition duration-500 group-hover:scale-105"
          imageOverride={previewColour ? style.colorImages[previewColour] : undefined}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#101c2d]/70 via-[#101c2d]/10 to-[#101c2d]/25" />
        <div className="absolute bottom-4 left-5 z-10 flex gap-1.5">
          {style.colors.map((color) => (
            <button
              key={color}
              type="button"
              className={`pointer-events-auto h-5 w-5 rounded-full border-2 shadow-sm transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${previewColour === color ? 'border-white scale-110' : 'border-white/40'}`}
              style={{ backgroundColor: styleColorHex[color] ?? style.accent }}
              title={`Preview ${style.name} in ${color}`}
              aria-label={`Preview ${style.name} in ${color}`}
              aria-pressed={previewColour === color}
              onClick={(event) => {
                event.stopPropagation();
                setPreviewColour(color);
              }}
              data-testid={`preview-${style.id}-${color.replaceAll(' ', '-')}`}
            />
          ))}
        </div>
      </div>
      <div className="p-5 flex flex-col h-[calc(100%-238px)] justify-between pointer-events-none">
        <div>
          <div className="flex items-start justify-between gap-3">
            <div><h3 className="display-font text-[23px] leading-tight text-foreground">{style.name}</h3><p className="mt-1.5 text-xs text-muted-foreground font-medium">{style.silhouette}</p></div>
            <div className="mt-1 text-primary transition-transform group-hover:translate-x-1" aria-hidden="true"><ChevronRight size={18} /></div>
          </div>
          <p className="mt-4 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{style.description}</p>
        </div>
        <div className="mt-5 flex flex-wrap gap-1.5">{style.tags.slice(0, featured ? 3 : 2).map((tag) => <span key={tag} className="rounded-full bg-secondary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[.1em] text-secondary-foreground">{tag}</span>)}</div>
      </div>
    </article>
  );
}

export function SavedMark({ saved }: { saved: boolean }) { return saved ? <Check size={15} /> : null; }